import { DeviceTypeId } from '@services/constants'
import { useConvertData } from '@services/device'
import {
  EStatusPill,
  useChargeStatusEnum,
  useParkingSpaceEnum,
} from '@services/device/chargingpoint'
import { useAllSessions } from '@services/session'
import { convertData } from '@shared/innmaxLib/services'
import { momentToString } from '@shared/innmaxLib/utils/webHelper'
import moment from 'moment'
import { pathOr } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'

// import { DeviceTypeId } from '@services/constants'
// import {
//   IElectricityMeter,
//   IElectricityMeterDetail,
// } from './electricitymeter.types'

export const useConvertChargingPoint = (res: any) => {
  const { byId } = useAllSessions()
  const { t } = useTranslation()
  const { byId: chargeStatusEnumById } = useChargeStatusEnum()

  const {
    deviceStatus,
    activeStr,
    sessionName,
    expiredDate,
    installTime,
    updateTime,
    locationNs,
    sessionPath,
  } = useConvertData()

  const dataSource = React.useMemo(
    () =>
      convertData<any>({
        converter: x => {
          return {
            ...x,
            ...deviceStatus(x),
            ...activeStr(x),
            ...sessionName(x),
            ...expiredDate(x),
            ...installTime(x),
            ...updateTime(x),
            ...locationNs(x),
            ...sessionPath(x),
            sessionName: pathOr(
              t('report:no area assigned'),
              [x.sessionId, 'name'],
              byId
            ),
            sessionNo: pathOr('-', [x.sessionId, 'sessionNo'], byId),
            properties: {
              ...x.properties,
            },
            connectors: x?.connectors?.map((ele: any) => ({
              ...ele,
              statusStr:
                ele?.status === 0
                  ? '可使用'
                  : ele?.status === 1
                  ? '充電中'
                  : '維修中',
              detailedStatus:
                ele.detailedStatus === EStatusPill.Finishing
                  ? EStatusPill.Occupy
                  : ele.detailedStatus,
              detailedStatusStr:
                chargeStatusEnumById[
                  ele.detailedStatus === EStatusPill.Finishing
                    ? EStatusPill.Occupy
                    : ele.detailedStatus
                ]?.label,
              chargeStartAtStr: ele.chargeStartAt
                ? momentToString(ele.chargeStartAt, 'HH:mm:ss')
                : '--',
            })),
          }
        },
      })(res),
    [res]
  )

  return dataSource
}

export const useConvertChargingPointDetail = (x?: any) => {
  const { byId } = useAllSessions()
  const { byKey } = useParkingSpaceEnum()
  const { t } = useTranslation()

  const {
    deviceStatus,
    activeStr,
    sessionName,
    expiredDate,
    installTime,
    updateTime,
    lastOnlineAt,
    expiredAt,
    installAt,
    switchLeakageStr,
    locationNs,
    sessionPath,
    businessDurationTime,
    lastUseTime,
  } = useConvertData()

  const { byId: chargeStatusEnumById } = useChargeStatusEnum()

  return React.useMemo(() => {
    if (!x) {
      return {}
    }

    return {
      ...x,
      ...deviceStatus(x),
      ...activeStr(x),
      ...sessionName(x),
      ...expiredDate(x),
      ...installTime(x),
      ...updateTime(x),
      ...locationNs(x),
      ...sessionPath(x),
      ...lastUseTime(x),
      ...businessDurationTime(x),
      deviceTypeId: DeviceTypeId[x.deviceType],
      sessionName: pathOr(
        t('report:no area assigned'),
        [x.sessionId, 'name'],
        byId
      ),
      sessionNo: pathOr('-', [x.sessionId, 'sessionNo'], byId),
      properties: {
        ...x.properties,
        ...switchLeakageStr(x),
        ...lastUseTime(x.properties),
        parkingSpaceType1Str:
          byKey[x?.properties?.parkingSpaceType1]?.label || '-',
        parkingSpaceType2Str:
          byKey[x?.properties?.parkingSpaceType2]?.label || '-',
      },
      connectors: x?.connectors.reduce((acc: any, connector: any) => {
        acc[connector.deviceId] = {
          ...connector,
          statusStr:
            connector?.status === 0
              ? '可使用'
              : connector?.status === 1
              ? '充電中'
              : '維修中',
          detailedStatus:
            connector.detailedStatus === EStatusPill.Finishing
              ? EStatusPill.Occupy
              : connector.detailedStatus,
          detailedStatusStr:
            chargeStatusEnumById[
              connector.detailedStatus === EStatusPill.Finishing
                ? EStatusPill.Occupy
                : connector.detailedStatus
            ]?.label,
          chargeStartAtStr: connector.chargeStartAt
            ? momentToString(connector.chargeStartAt, 'HH:mm:ss')
            : '--',
          properties: {
            ...connector.properties,
            lastUseTimeStr:
              connector?.properties?.lastUseTime &&
              moment(connector?.properties?.lastUseTime).format('YYYY/MM/DD'),
          },
        }
        return acc
      }, {}),
    }
  }, [x])
}

export const useChargingPointDetailConverter = () => {
  const { byId } = useAllSessions()
  const { byKey } = useParkingSpaceEnum()
  const { t } = useTranslation()

  const {
    deviceStatus,
    activeStr,
    sessionName,
    expiredDate,
    installTime,
    updateTime,
    lastOnlineAt,
    expiredAt,
    installAt,
    switchLeakageStr,
    locationNs,
    sessionPath,
    businessDurationTime,
    lastUseTime,
  } = useConvertData()

  const { byId: chargeStatusEnumById } = useChargeStatusEnum()

  return React.useCallback(
    (x?: any) => {
      if (!x) {
        return {}
      }

      return {
        ...x,
        ...deviceStatus(x),
        ...activeStr(x),
        ...sessionName(x),
        ...expiredDate(x),
        ...installTime(x),
        ...updateTime(x),
        ...locationNs(x),
        ...sessionPath(x),
        ...lastUseTime(x),
        ...businessDurationTime(x),
        deviceTypeId: DeviceTypeId[x.deviceType],
        sessionName: pathOr(
          t('report:no area assigned'),
          [x.sessionId, 'name'],
          byId
        ),
        sessionNo: pathOr('-', [x.sessionId, 'sessionNo'], byId),
        properties: {
          ...x.properties,
          ...switchLeakageStr(x),
          ...lastUseTime(x.properties),
          parkingSpaceType1Str:
            byKey[x?.properties?.parkingSpaceType1]?.label || '-',
          parkingSpaceType2Str:
            byKey[x?.properties?.parkingSpaceType2]?.label || '-',
        },
        connectors: x?.connectors.map((connector: any) => {
          return {
            ...connector,
            statusStr:
              connector?.status === 0
                ? '可使用'
                : connector?.status === 1
                ? '充電中'
                : '維修中',
            detailedStatusStr:
              chargeStatusEnumById[connector.detailedStatus]?.label,
            properties: {
              ...connector.properties,
              lastUseTimeStr:
                connector?.properties?.lastUseTime &&
                moment(connector?.properties?.lastUseTime).format('YYYY/MM/DD'),
            },
          }
        }),
      }
    },
    [byId, byKey]
  )
}
